import { memo, useState, useEffect } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { useIntl } from 'react-intl'

const LOADER_MAX_VALUE = 100

export interface FileLoaderProps {
  file: File
  withProgress?: boolean
  className?: string
  onLoaded: (fileSrc: string) => void
  onError: (error: string) => void
}

export const FileLoader = memo(function FileLoader(props: FileLoaderProps) {
  const intl = useIntl()
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const reader = new FileReader()

    reader.onprogress = (p) => {
      const loaded = (p.loaded / p.total) * LOADER_MAX_VALUE
      if (progress !== Math.ceil(loaded)) {
        requestAnimationFrame(() => {
          setProgress(loaded)
        })
      }
    }

    reader.onerror = () => {
      props.onError(intl.formatMessage({ id: 'file.messages.read-error' }))
    }

    reader.onload = () => {
      requestAnimationFrame(() => {
        setProgress(LOADER_MAX_VALUE)
      })

      props.onLoaded(reader.result as string)
    }

    reader.readAsDataURL(props.file)
    // EXPL: read the file once per `props.file`. `progress` is only read inside the progress
    // handler — listing it would re-run the reader on every progress tick (a read loop). `intl`
    // and the callbacks are captured at read time and should not re-trigger a re-read either.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.file])

  return props.withProgress ? (
    <CircularProgress variant="determinate" value={progress} className={props.className || ''} />
  ) : null
})

export default FileLoader
